// view/statusLine.js — createStatusLine: one message line, e.g. the tagging status in the documents popup footer
// (plan bugfix_2026-10-06_permission-tagging, phase fehler-sichtbar F1). Replaces tagstat.log (basic.js), which
// shows nothing since the footer lost #tagginglog (bug:tagstat-statusline-element-missing) and let every green
// message wipe out a red one (bug:tagstat-statusline-hides-save-errors).
//
// - success(msg): shown, fades after SUCCESS_VISIBLE_MS. Does NOT replace an error that is still showing.
// - error(msg):   stays until dismissed (×). Another error replaces the text and counts up: "… (2)".
//
// Returns { element, success(msg), error(msg), dismissError(), destroy() }. Messages are plain text (textContent).

import { createDisposers } from '../core/disposers.js';

export const SUCCESS_VISIBLE_MS = 3000;

const CLOSE_ICON = '<span class="topo2-ic topo2-ic--close"></span>';

export function createStatusLine({
    dismissLabel = 'Meldung schließen',   // fallback only — the caller passes the translated label (STATUS_LINE_DISMISS)
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (t) => clearTimeout(t),
} = {}) {
    const disposers = createDisposers();
    let errorCount = 0;
    let timer = null;

    const mk = (tag, cls) => { const e = document.createElement(tag); e.className = cls; return e; };

    // A span, so it sits in rows of buttons/spans without picking up `> div` layout rules of the host.
    const element = mk('span', 'topo2-status-line');
    element.setAttribute('role', 'status');
    element.setAttribute('aria-live', 'polite');
    const text = mk('span', 'topo2-status-line-text');
    const dismissBtn = mk('button', 'topo2-status-line-dismiss topo2-btn topo2-btn--icon topo2-btn--noborder topo2-btn--extrasmall topo2-btn--inset');
    dismissBtn.type = 'button';
    dismissBtn.setAttribute('aria-label', dismissLabel);
    dismissBtn.title = dismissLabel;
    dismissBtn.innerHTML = CLOSE_ICON;
    dismissBtn.hidden = true;
    element.append(text, dismissBtn);

    const onDismiss = () => dismissError();
    dismissBtn.addEventListener('click', onDismiss);
    disposers.add(() => dismissBtn.removeEventListener('click', onDismiss));
    disposers.add(() => cancelTimer());

    function cancelTimer() {
        if (timer !== null) { clearTimer(timer); timer = null; }
    }

    function show(message, kind) {
        text.textContent = message;
        text.title = message;   // the line is narrow and cuts long text with "…"
        element.dataset.kind = kind;
    }

    function clear() {
        text.textContent = '';
        text.title = '';
        delete element.dataset.kind;
        dismissBtn.hidden = true;
    }

    function success(message) {
        if (!message || errorCount > 0) { return; }   // an error stays visible until dismissed
        cancelTimer();
        show(message, 'success');
        timer = setTimer(() => { timer = null; clear(); }, SUCCESS_VISIBLE_MS);
    }

    function error(message) {
        if (!message) { return; }
        cancelTimer();
        errorCount += 1;
        show(errorCount > 1 ? `${message} (${errorCount})` : message, 'error');
        dismissBtn.hidden = false;
    }

    function dismissError() {
        errorCount = 0;
        cancelTimer();
        clear();
    }

    return {
        element,
        success,
        error,
        dismissError,
        destroy() { disposers.dispose(); },
    };
}
